import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, DownloadIcon, CopyIcon, CheckIcon } from 'lucide-react';
interface SurveyData {
  dealFocus: string;
  budget: string;
  timeline: string;
  firstTimeInvestor: string;
  importantFactors: string[];
  preferredArea: string;
  additionalContext: string;
}
export function DealReport() {
  const [surveyData, setSurveyData] = useState<SurveyData>({
    dealFocus: '',
    budget: '',
    timeline: '',
    firstTimeInvestor: '',
    importantFactors: [],
    preferredArea: '',
    additionalContext: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [investorId, setInvestorId] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const generateInvestorId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = 'CBF-';
    for (let i = 0; i < 4; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };
  const handleCheckboxChange = (factor: string) => {
    setSurveyData(prev => ({
      ...prev,
      importantFactors: prev.importantFactors.includes(factor) ? prev.importantFactors.filter(f => f !== factor) : [...prev.importantFactors, factor]
    }));
  };
  const validateForm = () => {
    const newErrors: string[] = [];
    if (!surveyData.dealFocus) newErrors.push('Please select your deal focus');
    if (!surveyData.budget) newErrors.push('Please select your budget range');
    if (!surveyData.timeline) newErrors.push('Please select your investment timeline');
    if (!surveyData.firstTimeInvestor) newErrors.push("Please indicate if you're a first-time Cabo investor");
    if (surveyData.importantFactors.length === 0) newErrors.push('Please select at least one important factor');
    if (!surveyData.preferredArea) newErrors.push('Please select your preferred area');
    setErrors(newErrors);
    return newErrors.length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const id = generateInvestorId();
    setInvestorId(id);
    setIsSubmitted(true);
    console.log('Survey Data:', surveyData);
    console.log('Investor ID:', id);
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(investorId);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };
  if (isSubmitted) {
    return <section id="deal-report" className="py-24 bg-charcoal" data-id="element-134" data-mp="1jfvu9d1bsvq3">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-135" data-mp="1jgdxitmeoa7i">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="bg-navy rounded-lg p-8 sm:p-12 border border-gold" data-id="element-136" data-mp="1enual61tw2q1">
            <div className="text-center mb-8" data-id="element-137" data-mp="2an43vn6rsj6b">
              <CheckIcon className="text-gold mx-auto mb-4" size={64} data-id="element-138" data-mp="2aofjck9kvfd9" />
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4" data-id="element-139" data-mp="2eo63w68igz9c">
                Survey Complete
              </h2>
              <p className="text-white/80 text-lg" data-id="element-140" data-mp="2jm7owwphz8ng">
                Your Investor ID has been generated. Save this for future
                reference.
              </p>
            </div>

            <div className="bg-charcoal rounded-lg p-6 mb-8" data-id="element-141" data-mp="2tl70an1ndie5">
              <p className="text-white/60 text-sm mb-2" data-id="element-142" data-mp="2xnbmx1tohera">Your Investor ID</p>
              <div className="flex items-center justify-between bg-navy/50 rounded px-4 py-3 border border-gold/30" data-id="element-143" data-mp="2xl7dy5gx9pk5">
                <span className="text-gold font-mono text-2xl font-bold" data-id="element-144" data-mp="37d3ls88t8u1k">
                  {investorId}
                </span>
                <button onClick={copyToClipboard} className="text-gold hover:text-gold-light transition-colors" title="Copy to clipboard" data-id="element-145" data-mp="3cdizx20x25kl">
                  {isCopied ? <CheckIcon size={24} data-id="element-146" data-mp="hvz58x0ib044" /> : <CopyIcon size={24} data-id="element-147" data-mp="hv77pgncmsb7" />}
                </button>
              </div>
            </div>

            <div className="bg-gold/10 border border-gold/30 rounded-lg p-6 mb-8" data-id="element-148" data-mp="cwmgu7tzlkzf">
              <h3 className="text-gold font-semibold mb-3 flex items-center" data-id="element-149" data-mp="rpfy9ystkb6q">
                <FileTextIcon className="mr-2" size={20} data-id="element-150" data-mp="mos9zkvz68g7" />
                Important: Save Your Investor ID
              </h3>
              <ul className="text-white/80 space-y-2 text-sm" data-id="element-151" data-mp="3v66c2zun7siz">
                <li data-id="element-152" data-mp="3q6afhlo9z3dg">
                  • Screenshot or write down your Investor ID:{' '}
                  <span className="text-gold font-mono font-semibold" data-id="element-153" data-mp="80f9caly01fd">
                    {investorId}
                  </span>
                </li>
                <li data-id="element-154" data-mp="4054j19vzh23">• If you ever contact us, reference this ID</li>
                <li data-id="element-155" data-mp="1jcy77mqkhykg">
                  • We'll pull exactly what you told us you're looking for — no
                  need to start over
                </li>
                <li data-id="element-156" data-mp="1mvxeieeejqzx">
                  • This ID is unique to your preferences and investment
                  criteria
                </li>
              </ul>
            </div>

            <button onClick={() => alert('Deal Report PDF download initiated')} className="w-full bg-gold hover:bg-gold-light text-navy px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-3" data-id="element-157" data-mp="1cyak0jkmdquu">
              <DownloadIcon size={24} data-id="element-158" data-mp="25nq37wmfuq1u" />
              Download Deal Report
            </button>

            <p className="text-white/60 text-center text-sm mt-6" data-id="element-159" data-mp="2al9hgwj4z2ts">
              The report contains full investment analysis, financial models,
              and ongoing deal rankings.
            </p>
          </motion.div>
        </div>
      </section>;
  }
  return <section id="deal-report" className="py-24 bg-charcoal" data-id="element-160" data-mp="2okrajo3gt28y">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-161" data-mp="2tc92xnrf9uib">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-162" data-mp="2tdty9flk9hh8">
          <FileTextIcon className="text-gold mx-auto mb-4" size={48} data-id="element-163" data-mp="2p99nv724r3vi" />
          <h2 className="text-4xl sm:text-5xl font-serif font-bold text-white mb-6" data-id="element-164" data-mp="2k9n7ior097ih">
            Get the Full Cabo Deal Flow Report
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto mb-4" data-id="element-165" data-mp="2fhvnyy69o21u">
            This report contains full investment analysis, financial models, and
            ongoing deal rankings — updated as the market shifts.
          </p>
          <p className="text-white/60 max-w-2xl mx-auto" data-id="element-166" data-mp="26g5od528dwsz">
            To access it, complete a short investor survey (7 questions, ~3
            minutes). The survey allows us to continuously improve the quality
            of deal flow we present. When we know what the market is looking
            for, we source better deals.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-navy rounded-lg p-8 sm:p-10 border border-white/10" data-id="element-167" data-mp="h01xug3p56pf">
          <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-8" data-id="element-168" data-mp="3ugj6xl1wn72t">
            <p className="text-gold text-sm font-semibold" data-id="element-169" data-mp="2b5v8ykeslw7">
              ✓ We do NOT ask for your name or phone number. The survey is
              anonymous unless you choose to share contact info.
            </p>
          </div>

          {errors.length > 0 && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6" data-id="element-170" data-mp="3ars9xpzmvejs">
              <p className="text-red-400 font-semibold mb-2" data-id="element-171" data-mp="244pxhes0or0u">
                Please complete the following:
              </p>
              <ul className="text-red-300 text-sm space-y-1" data-id="element-172" data-mp="1z6emv1twechk">
                {errors.map((error, index) => <li key={index} data-id="element-173" data-mp="2e3x7pt3gg5jq" data-mpi={index}>• {error}</li>)}
              </ul>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-174" data-mp="292zpjgig3bi8">
            {/* Deal Focus */}
            <div data-id="element-175" data-mp="2n2982mu4ihpd">
              <label className="block text-white font-semibold mb-3" data-id="element-176" data-mp="2n1r5z9ks599j">
                1. What type of deal are you primarily focused on?
              </label>
              <div className="space-y-2" data-id="element-177" data-mp="2wtn4umuox3et">
                {['Cash Flow', 'Equity Appreciation', 'Land', 'Commercial', 'All of the Above'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-178" data-mp="31sgjiilpr0gk">
                    <input type="radio" name="dealFocus" value={option} checked={surveyData.dealFocus === option} onChange={e => setSurveyData({
                  ...surveyData,
                  dealFocus: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-179" data-mp="3keo0154o14ki" />
                    {option}
                  </label>)}
              </div>
            </div>

            {/* Budget Range */}
            <div data-id="element-180" data-mp="2wqo7q0rph63t">
              <label className="block text-white font-semibold mb-3" data-id="element-181" data-mp="2ws7ortl6rl6n">
                2. What is your investment budget range?
              </label>
              <div className="space-y-2" data-id="element-182" data-mp="2itrczo8gv4hn">
                {['$250K - $500K', '$500K - $1M', '$1M - $3M', '$3M+'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-183" data-mp="11boxtccxyl8n">
                      <input type="radio" name="budget" value={option} checked={surveyData.budget === option} onChange={e => setSurveyData({
                  ...surveyData,
                  budget: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-184" data-mp="rf3ncbi5khxf" />
                      {option}
                    </label>)}
              </div>
            </div>

            {/* Timeline */}
            <div data-id="element-185" data-mp="27t29dok951p4">
              <label className="block text-white font-semibold mb-3" data-id="element-186" data-mp="27tlsh8ln1w4q">
                3. What is your investment timeline?
              </label>
              <div className="space-y-2" data-id="element-187" data-mp="3sguw2zkxdluu">
                {['Ready Now', '3-6 Months', '6-12 Months', '12+ Months'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-188" data-mp="54ty3s6kln27">
                      <input type="radio" name="timeline" value={option} checked={surveyData.timeline === option} onChange={e => setSurveyData({
                  ...surveyData,
                  timeline: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-189" data-mp="39jbl3qewpt5s" />
                      {option}
                    </label>)}
              </div>
            </div>

            {/* First-time Investor */}
            <div data-id="element-190" data-mp="3rlr408153q9w">
              <label className="block text-white font-semibold mb-3" data-id="element-191" data-mp="3rl7kwnzr6vua">
                4. Are you a first-time Cabo investor or have you invested in
                Cabo before?
              </label>
              <div className="space-y-2" data-id="element-192" data-mp="4gjckgnl3pbc">
                {['First-time Cabo investor', 'Have invested in Cabo before'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-193" data-mp="37zkzibda11by">
                    <input type="radio" name="firstTimeInvestor" value={option} checked={surveyData.firstTimeInvestor === option} onChange={e => setSurveyData({
                  ...surveyData,
                  firstTimeInvestor: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-194" data-mp="3how71tkilxxq" />
                    {option}
                  </label>)}
              </div>
            </div>

            {/* Important Factors */}
            <div data-id="element-195" data-mp="1ypohzdmbseyh">
              <label className="block text-white font-semibold mb-3" data-id="element-196" data-mp="1yq6n6zqyu3ij">
                5. What is most important to you? (Select all that apply)
              </label>
              <div className="space-y-2" data-id="element-197" data-mp="1twveet2lfcnz">
                {['Cash Flow', 'Equity Upside', 'Long-term Hold', 'Short-term Flip', 'Land Banking', 'Commercial'].map(factor => <label key={factor} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-198" data-mp="1g101r4ysext1">
                    <input type="checkbox" checked={surveyData.importantFactors.includes(factor)} onChange={() => handleCheckboxChange(factor)} className="mr-3 w-4 h-4 text-gold rounded" data-id="element-199" data-mp="3b2vb3vmgasnz" />
                    {factor}
                  </label>)}
              </div>
            </div>

            {/* Preferred Area */}
            <div data-id="element-200" data-mp="2wd7c511qs63f">
              <label className="block text-white font-semibold mb-3" data-id="element-201" data-mp="2wdphcn6dtunh">
                6. Is there a specific area of Cabo you prefer?
              </label>
              <select value={surveyData.preferredArea} onChange={e => setSurveyData({
              ...surveyData,
              preferredArea: e.target.value
            })} className="w-full bg-charcoal text-white border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-gold" data-id="element-202" data-mp="2ifr11c8i3o5c">
                <option value="" data-id="element-203" data-mp="qq85lk8uynwg">Select an area...</option>
                <option value="Marina" data-id="element-204" data-mp="qrt0jaz348uu">Marina</option>
                <option value="Corridor" data-id="element-205" data-mp="14i6blm79w3oe">Corridor</option>
                <option value="East Cape" data-id="element-206" data-mp="14jr5d9lrjil7">East Cape</option>
                <option value="Pedregal" data-id="element-207" data-mp="zpvbai8mefvb">Pedregal</option>
                <option value="Pacific Side" data-id="element-208" data-mp="1efu7ifgm7h85">Pacific Side</option>
                <option value="Open to All" data-id="element-209" data-mp="1ee9eiu9y86ce">Open to All</option>
              </select>
            </div>

            {/* Additional Context */}
            <div data-id="element-210" data-mp="1ng8v5lh1x8et">
              <label className="block text-white font-semibold mb-3" data-id="element-211" data-mp="1ngscp12shusb">
                7. Any additional context about what you're looking for?
                (Optional)
              </label>
              <textarea value={surveyData.additionalContext} onChange={e => setSurveyData({
              ...surveyData,
              additionalContext: e.target.value
            })} rows={4} className="w-full bg-charcoal text-white border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-gold resize-none" placeholder="Share any specific requirements, preferences, or questions..." data-id="element-212" data-mp="3p7c6kcyrmr0f" />
            </div>

            <button type="submit" className="w-full bg-gold hover:bg-gold-light text-navy px-8 py-4 rounded-lg font-bold text-lg transition-colors" data-id="element-213" data-mp="274i8c70fu1eq">
              Complete Survey & Get Deal Report
            </button>
          </form>
        </motion.div>
      </div>
    </section>;
}